import { useMeStore } from '@/stores/me';
import { t } from '@/i18n';

/**
 * cms-api client. Auth is JWT bearer:
 *
 *   - access token sent as `Authorization: Bearer <jwt>` on every call
 *   - 401 on /api/admin/* → try one refresh against /admin/refresh, then retry
 *   - if refresh fails, clear local state and bounce to /login
 *   - 422 → throw ApiValidationError so callers can render per-field errors
 *   - 204 → resolve with `undefined`
 *
 * No cookies. No CSRF. CORS preflight only needs `Authorization` and
 * `Content-Type` in `allow-headers`.
 */
// Empty `VITE_API_BASE` = same-origin (admin's nginx proxies /api/* to
// cms-api). The fallback hostname is the local in-cluster admin host,
// which proxies to cms-api the same way.
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
    constructor(
        public status: number,
        message: string,
        public body?: unknown,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

export class ApiValidationError extends ApiError {
    constructor(public errors: Record<string, string[]>, message = t('ui.errors.validation')) {
        super(422, message, errors);
        this.name = 'ApiValidationError';
    }
}

interface ApiOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    query?: Record<string, string | number | boolean | undefined | null>;
    headers?: Record<string, string>;
    /** Skip the Authorization header (for /admin/login + /admin/refresh themselves). */
    noAuth?: boolean;
    /** Internal: prevent infinite refresh-retry loops. */
    _retried?: boolean;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
    const url = buildUrl(path, opts.query);
    const method = opts.method ?? 'GET';
    const headers: Record<string, string> = {
        Accept: 'application/json',
        ...opts.headers,
    };

    if (opts.body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }
    if (!opts.noAuth) {
        const me = useMeStore();
        if (me.accessToken) {
            headers['Authorization'] = `Bearer ${me.accessToken}`;
        }
    }

    let res: Response;
    try {
        res = await fetch(url, {
            method,
            headers,
            body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        });
    } catch {
        // fetch only rejects when no response came back at all (offline, DNS,
        // CORS, connection reset). The browser's own text for that ("Failed
        // to fetch") is English and says nothing useful, so say it ourselves.
        // Status 0 is what the browser reports for such a request.
        throw new ApiError(0, t('ui.errors.network'));
    }

    // 401: try one silent refresh, then retry. If refresh fails, fall
    // through and let the caller see the 401.
    if (res.status === 401 && !opts._retried && !opts.noAuth) {
        const me = useMeStore();
        const refreshed = await me.refresh();
        if (refreshed) {
            return api(path, { ...opts, _retried: true });
        }
        me.clearTokens();
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.assign('/login');
        }
    }

    if (res.status === 204) {
        return undefined as T;
    }

    const contentType = res.headers.get('Content-Type') ?? '';
    const body: unknown = contentType.includes('application/json')
        ? await res.json().catch(() => null)
        : await res.text();

    if (!res.ok) {
        if (res.status === 422 && body && typeof body === 'object' && 'errors' in body) {
            const errors = (body as { errors: Record<string, string[]> }).errors;
            throw new ApiValidationError(errors);
        }
        const message = (body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string')
            ? String((body as { error: string }).error)
            : `HTTP ${res.status}`;
        throw new ApiError(res.status, message, body);
    }

    return body as T;
}

/**
 * Multipart / non-JSON requests. Same auth semantics as api(); caller
 * controls the body and Content-Type.
 */
export async function apiRaw(path: string, init: RequestInit & { skipAuth?: boolean } = {}): Promise<Response> {
    const me = useMeStore();
    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) headers.set('Accept', 'application/json');
    if (!init.skipAuth && me.accessToken) {
        headers.set('Authorization', `Bearer ${me.accessToken}`);
    }
    return fetch(API_BASE + path, { ...init, headers });
}

/**
 * Absolute URL of an API path, or of a root-relative URL the API handed
 * back, on the host the api() calls go to. For what the browser opens
 * itself (a download link), where a bare root-relative URL would land on
 * the admin's own host whenever VITE_API_BASE points elsewhere.
 */
export function apiUrl(path: string): string {
    return buildUrl(path);
}

function buildUrl(path: string, query?: ApiOptions['query']): string {
    // `new URL` needs an absolute URL. Same-origin (API_BASE='') means
    // we resolve against window.location so the result stays absolute
    // for fetch but lives on the current origin.
    const base = API_BASE === '' && typeof window !== 'undefined'
        ? window.location.origin
        : API_BASE;
    const url = new URL(base + path);
    if (query) {
        for (const [k, v] of Object.entries(query)) {
            if (v !== undefined && v !== null && v !== '') {
                url.searchParams.set(k, String(v));
            }
        }
    }
    return url.toString();
}
